import { useState, useEffect, useCallback } from 'react';
import { Play } from 'lucide-react';

/**
 * Detect if user is on a slow connection or has data-saver enabled.
 * Returns true if videos should NOT autoload.
 */
export function useSlowConnection() {
  const [isSlow, setIsSlow] = useState(() => {
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!conn) return false;
    if (conn.saveData) return true;
    const type = conn.effectiveType;
    return type === '2g' || type === 'slow-2g';
  });

  useEffect(() => {
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!conn) return;
    const update = () => {
      setIsSlow(conn.saveData || conn.effectiveType === '2g' || conn.effectiveType === 'slow-2g');
    };
    conn.addEventListener('change', update);
    return () => conn.removeEventListener('change', update);
  }, []);

  return isSlow;
}

/**
 * SmartVideo — Renders video or thumbnail+button based on connection speed.
 * On slow connections: shows thumbnail with "Load video" button.
 * On fast connections: renders video normally.
 */
export function SmartVideo({ src, thumbnail, className = '', poster, autoPlay = true, loop = true, controls = false, ...props }) {
  const isSlow = useSlowConnection();
  const [userOptedIn, setUserOptedIn] = useState(false);
  const thumbSrc = thumbnail || poster || '';

  const shouldLoadVideo = !isSlow || userOptedIn;

  if (!shouldLoadVideo && thumbSrc) {
    return (
      <div className={`relative ${className}`} style={{ background: '#000' }}>
        <img src={thumbSrc} alt="" className="w-full h-full object-cover" />
        <button
          onClick={() => setUserOptedIn(true)}
          className="absolute inset-0 flex items-center justify-center bg-black/40 active:bg-black/60 transition-colors"
          data-testid="load-video-btn"
        >
          <div className="w-14 h-14 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 flex items-center justify-center">
            <Play size={24} className="text-white ml-0.5" fill="white" />
          </div>
        </button>
      </div>
    );
  }

  if (!shouldLoadVideo && !thumbSrc) {
    return (
      <div className={`relative flex items-center justify-center ${className}`} style={{ background: '#111' }}>
        <button
          onClick={() => setUserOptedIn(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 border border-white/15 text-white/70 text-sm font-heading font-bold uppercase tracking-wider active:scale-95 transition-all"
          data-testid="load-video-btn"
        >
          <Play size={16} /> Load Video
        </button>
      </div>
    );
  }

  return (
    <video
      src={src}
      className={className}
      poster={poster || thumbnail}
      autoPlay={autoPlay}
      loop={loop}
      muted
      playsInline
      controls={controls}
      controlsList="nodownload nofullscreen noremoteplayback"
      disablePictureInPicture
      onContextMenu={e => e.preventDefault()}
      {...props}
    />
  );
}

/**
 * SmartVideoThumb — For list views (quick swap, exercise picker).
 * On slow connection: only shows thumbnail, never loads video.
 * On fast connection: shows thumbnail (no video in lists for performance).
 */
export function SmartVideoThumb({ thumbnail, videoUrl, className = '' }) {
  if (thumbnail) {
    return <img src={thumbnail} alt="" className={`object-cover ${className}`} loading="lazy" />;
  }
  if (videoUrl) {
    const isSlow = typeof navigator !== 'undefined' && navigator.connection && 
      (navigator.connection.saveData || navigator.connection.effectiveType === '2g' || navigator.connection.effectiveType === 'slow-2g');
    if (isSlow) {
      return <div className={`bg-[#1A1A1A] flex items-center justify-center ${className}`}><Play size={16} className="text-white/30" /></div>;
    }
    return <video src={`${videoUrl}#t=0.1`} className={`object-cover ${className}`} muted playsInline preload="metadata" />;
  }
  return null;
}
